import React, { useState, useEffect } from 'react';
import pushNotificationManager from '../utils/pushNotifications';
import './NotificationSettings.css';

const NotificationSettings = ({ onClose, showAsModal = true }) => {
  // Get user from localStorage (same as other components)
  const [user, setUser] = useState(() => {
    const userStr = localStorage.getItem('dizeeUser');
    return userStr ? JSON.parse(userStr) : null;
  });
  const [loading, setLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    reminders: true,
    bookingReminders: true,
    catalogReminders: true,
    releaseReminders: true,
    teamUpdates: true,
    analytics: true,
    messages: true,
    maxNotificationsPerDay: 20,
    batchNotifications: false
  });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkNotificationSupport();
    loadPreferences();
    checkSubscriptionStatus();
  }, []);

  const checkNotificationSupport = () => {
    const supported = pushNotificationManager.isSupported();
    setIsSupported(supported);
    
    if (supported) {
      const enabled = pushNotificationManager.isEnabled();
      setIsEnabled(enabled);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const subscribed = await pushNotificationManager.isSubscribed();
      setIsSubscribed(subscribed);
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const loadPreferences = async () => {
    if (!user?._id) return;

    try {
      const data = await pushNotificationManager.getPreferences(user._id);
      if (data.success && data.preferences) {
        setPreferences(data.preferences);
      }
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const handleEnableNotifications = async () => {
    if (!user?._id || !user?.email) {
      setError('User information not available. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await pushNotificationManager.subscribe(user._id, user.email);
      setIsSubscribed(true);
      setIsEnabled(true);
      
      // Show success message and send test notification
      setTimeout(() => {
        pushNotificationManager.sendTestNotification(user._id);
      }, 1000);
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setError(error.message || 'Failed to enable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    if (!user?._id) return;

    setLoading(true);
    setError(null);

    try {
      await pushNotificationManager.unsubscribe(user._id);
      setIsSubscribed(false);
      setIsEnabled(false);
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setError('Failed to disable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    if (!user?._id) return;

    const newPreferences = {
      ...preferences,
      [key]: value
    };

    setPreferences(newPreferences);

    try {
      await pushNotificationManager.updatePreferences(user._id, newPreferences);
    } catch (error) {
      console.error('Error updating preferences:', error);
      setError('Failed to save preferences. Please try again.');
    }
  };

  const handleSendTestNotification = async () => {
    if (!user?._id) return;

    setLoading(true);
    setError(null);

    try {
      await pushNotificationManager.sendTestNotification(user._id);
      // Optionally show a success message
    } catch (error) {
      console.error('Error sending test notification:', error);
      setError('Failed to send test notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const NotificationToggle = ({ label, description, value, onChange, disabled = false }) => (
    <div className="notification-setting-row">
      <div className="notification-setting-info">
        <div className="notification-setting-label">{label}</div>
        {description && (
          <div className="notification-setting-description">{description}</div>
        )}
      </div>
      <label className="notification-toggle">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled || !isSubscribed}
        />
        <span className="notification-toggle-slider"></span>
      </label>
    </div>
  );

  const renderContent = () => {
    if (!isSupported) {
      return (
        <div className="notification-unsupported">
          <div className="notification-icon">🔕</div>
          <h3>Notifications Not Supported</h3>
          <p>Your browser doesn't support push notifications. Try using a modern browser like Chrome, Firefox, or Safari.</p>
        </div>
      );
    }

    return (
      <div className="notification-settings-content">
        {/* Status Section */}
        <div className="notification-status-card">
          <div className="notification-status-header">
            <div className="notification-status-icon">
              {isSubscribed ? '🔔' : '🔕'}
            </div>
            <div>
              <h3 className="notification-status-title">
                {isSubscribed ? 'Notifications Enabled' : 'Enable Push Notifications'}
              </h3>
              <p className="notification-status-subtitle">
                {isSubscribed 
                  ? 'You\'ll receive reminders and important updates'
                  : 'Get notified about reminders, bookings, and releases'
                }
              </p>
            </div>
          </div>

          {error && (
            <div className="notification-error">
              <span className="notification-error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="notification-action">
            {!isSubscribed ? (
              <button
                className="notification-btn notification-btn-primary"
                onClick={handleEnableNotifications}
                disabled={loading}
              >
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </button>
            ) : (
              <div className="notification-action-group">
                <button
                  className="notification-btn notification-btn-secondary"
                  onClick={handleSendTestNotification}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Test'}
                </button>
                <button
                  className="notification-btn notification-btn-danger"
                  onClick={handleDisableNotifications}
                  disabled={loading}
                >
                  Disable
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        {isSubscribed && stats && (
          <div className="notification-stats">
            <div className="notification-stat">
              <div className="notification-stat-value">{stats.totalSent || 0}</div>
              <div className="notification-stat-label">Sent</div>
            </div>
            <div className="notification-stat">
              <div className="notification-stat-value">{stats.totalClicked || 0}</div>
              <div className="notification-stat-label">Clicked</div>
            </div>
            <div className="notification-stat">
              <div className="notification-stat-value">
                {stats.totalSent > 0 
                  ? Math.round((stats.totalClicked / stats.totalSent) * 100)
                  : 0}%
              </div>
              <div className="notification-stat-label">Rate</div>
            </div>
          </div>
        )}

        {/* Preferences Section */}
        {isSubscribed && (
          <>
            <div className="notification-section">
              <h4 className="notification-section-title">Reminder Notifications</h4>
              <div className="notification-settings-list">
                <NotificationToggle
                  label="All Reminders"
                  description="Master switch for all reminder notifications"
                  value={preferences.reminders}
                  onChange={(value) => handlePreferenceChange('reminders', value)}
                />
                <NotificationToggle
                  label="Booking Reminders"
                  description="Offers, contracts, show advances, and settlements"
                  value={preferences.bookingReminders}
                  onChange={(value) => handlePreferenceChange('bookingReminders', value)}
                  disabled={!preferences.reminders}
                />
                <NotificationToggle
                  label="Catalog Reminders"
                  description="Music releases, distributor uploads, and pitching"
                  value={preferences.catalogReminders}
                  onChange={(value) => handlePreferenceChange('catalogReminders', value)}
                  disabled={!preferences.reminders}
                />
              </div>
            </div>

            <div className="notification-section">
              <h4 className="notification-section-title">Other Notifications</h4>
              <div className="notification-settings-list">
                <NotificationToggle
                  label="Team Updates"
                  description="Team invitations and collaboration updates"
                  value={preferences.teamUpdates}
                  onChange={(value) => handlePreferenceChange('teamUpdates', value)}
                />
                <NotificationToggle
                  label="Analytics Alerts"
                  description="Important metrics and performance updates"
                  value={preferences.analytics}
                  onChange={(value) => handlePreferenceChange('analytics', value)}
                />
                <NotificationToggle
                  label="Messages"
                  description="Direct messages and important communications"
                  value={preferences.messages}
                  onChange={(value) => handlePreferenceChange('messages', value)}
                />
              </div>
            </div>

            <div className="notification-section">
              <h4 className="notification-section-title">Notification Behavior</h4>
              <div className="notification-settings-list">
                <div className="notification-setting-row">
                  <div className="notification-setting-info">
                    <div className="notification-setting-label">Daily Limit</div>
                    <div className="notification-setting-description">
                      Maximum notifications per day
                    </div>
                  </div>
                  <select
                    className="notification-select"
                    value={preferences.maxNotificationsPerDay}
                    onChange={(e) => handlePreferenceChange('maxNotificationsPerDay', parseInt(e.target.value))}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="999">Unlimited</option>
                  </select>
                </div>
                <NotificationToggle
                  label="Batch Notifications"
                  description="Group multiple notifications together (coming soon)"
                  value={preferences.batchNotifications}
                  onChange={(value) => handlePreferenceChange('batchNotifications', value)}
                  disabled={true}
                />
              </div>
            </div>
          </>
        )}

        {/* Info Section */}
        <div className="notification-info">
          <h4 className="notification-info-title">About Push Notifications</h4>
          <ul className="notification-info-list">
            <li>Works even when the DIZEE app is closed</li>
            <li>Reminders for bookings, releases, and important deadlines</li>
            <li>You can customize which types of notifications you receive</li>
            <li>Your notification preferences sync across all devices</li>
            <li>You can disable notifications at any time</li>
          </ul>
        </div>
      </div>
    );
  };

  if (!showAsModal) {
    return renderContent();
  }

  return (
    <div className="notification-settings-overlay" onClick={onClose}>
      <div className="notification-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notification-settings-header">
          <h2 className="notification-settings-title">Push Notifications</h2>
          <button className="notification-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default NotificationSettings;

